import { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { VEHICLE_TYPES } from '../constants/vehicleTypes';

interface MultiVehicleTypeSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

export function MultiVehicleTypeSelect({
  value,
  onChange,
  placeholder = 'Araç tipi seçiniz',
}: MultiVehicleTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (type: string) => {
    const next = value.includes(type) ? value.filter((v) => v !== type) : [...value, type];
    onChange(next);
  };

  const remove = (type: string) => onChange(value.filter((v) => v !== type));

  return (
    <div ref={ref} className="relative">
      <div
        className="flex min-h-[2.5rem] cursor-pointer flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-emerald-400"
        onClick={() => setOpen(!open)}
      >
        {value.length === 0 && (
          <span className="text-slate-400">{placeholder}</span>
        )}
        {value.map((type) => (
          <span
            key={type}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800"
          >
            {type}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(type); }}
              className="ml-0.5 rounded-full p-0.5 hover:bg-emerald-200"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-slate-400" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {VEHICLE_TYPES.map((type) => (
            <label
              key={type}
              className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={value.includes(type)}
                onChange={() => toggle(type)}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              {type}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
